import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps, useModal } from '@pcmp/react-providers';
import ListView, { ListViewItem } from '@ingka/list-view';
import { Sheets, ModalHeader, ModalFooter } from '@ingka/modal';

import { useUser } from '@/redux/features/session/hooks';
import LanguageSwitcher from '@/components/helpers/language-switcher';
import PermissionsSider from '../permissions';
import { useContentTracking } from '@/lib/hooks/tracking';
import { UserProfile } from '@/components/data/user-profile';
import FindLocationSider from '../find-location';
import {
  useActiveLocation,
  useActiveLocationPermissions,
  useShoppingBag,
} from '@/redux/features/shopping-bag/hooks';
import OrderOnBehalfSider from '../order-on-behalf';
import ChangeThemeSider from '../change-theme';
import { useAppConfig } from '@/redux/features/app-config/hooks';
import CoWorkerIcon from '@ingka/ssr-icon/paths/co-worker';
import LocationPinIcon from '@ingka/ssr-icon/paths/location-pin';
import DarkModeIcon from '@ingka/ssr-icon/paths/dark-mode';
import ShieldIcon from '@ingka/ssr-icon/paths/shield-checkmark';
import InformationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import HelpCenterSider from '../help-center';
import { useModalGoToProfile } from '@/lib/hooks/modal';

type Section = 'permissions' | 'user' | 'location' | 'theme' | 'helpCenter';

const SIDER_SECTIONS: Record<Section, FC<ModalComponentBaseProps>> = {
  permissions: PermissionsSider,
  user: OrderOnBehalfSider,
  location: FindLocationSider,
  theme: ChangeThemeSider,
  helpCenter: HelpCenterSider,
};

type UserProfileSiderProps = ModalComponentBaseProps;

export const UserProfileSider: FC<UserProfileSiderProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const user = useUser();
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  const { openModal, closeModal } = useModal();
  const { canOrderOnBehalf } = useActiveLocationPermissions();
  const goBack = useModalGoToProfile();
  // Data
  const { data: location } = useActiveLocation();
  const shoppingBag = useShoppingBag();
  const { darkMode } = useAppConfig();
  // Event handlers
  const goToSection = (section: Section) => async () => {
    await closeModal(0);
    await openModal({
      component: SIDER_SECTIONS[section],
      props: {
        onBack: goBack,
      },
    });
  };

  useEffect(() => {
    trackContent('user-profile', { type: 'sider' });
  }, []);

  return (
    <Sheets
      {...rest}
      data-testid="userProfileSider"
      alignment="right"
      aria-label={t('label.code.user')}
      footer={
        <ModalFooter>
          <LanguageSwitcher />
        </ModalFooter>
      }
      header={<ModalHeader ariaCloseTxt={t('label.close')} />}
      size="small"
    >
      {user && <UserProfile email={user.email} displayName={user.name} />}

      <ListView id="report">
        <ListViewItem
          data-testid="permissionsBtn"
          size="medium"
          inset
          leadingIcon={ShieldIcon}
          emphasised
          title={t('page.permissions.shortTitle')}
          control="navigational"
          controlIcon="chevronRight"
          onClick={goToSection('permissions')}
        />

        <ListViewItem
          data-testid="locationBtn"
          size="medium"
          inset
          leadingIcon={LocationPinIcon}
          emphasised
          title={t('label.currentLocation')}
          description={location?.name || t('label.noLocationSet')}
          control="navigational"
          controlIcon="chevronRight"
          onClick={goToSection('location')}
        />

        <ListViewItem
          data-testid="orderOnBehalfBtn"
          size="medium"
          inset
          leadingIcon={CoWorkerIcon}
          emphasised
          title={t('label.orderOnBehalfOf')}
          description={
            shoppingBag?.onBehalfOf?.displayName || t('label.myself')
          }
          control="navigational"
          controlIcon="chevronRight"
          onClick={goToSection('user')}
          disabled={!canOrderOnBehalf}
        />

        <ListViewItem
          data-testid="helpCenterBtn"
          size="medium"
          inset
          leadingIcon={InformationCircleIcon}
          emphasised
          title={t('page.helpCenter.title')}
          description={t('page.helpCenter.description')}
          control="navigational"
          controlIcon="chevronRight"
          onClick={goToSection('helpCenter')}
        />

        <ListViewItem
          data-testid="themeBtn"
          size="medium"
          inset
          leadingIcon={DarkModeIcon}
          emphasised
          title={t('label.changeTheme.title')}
          description={t(
            `label.changeTheme.${darkMode ? 'darkMode' : 'lightMode'}`
          )}
          control="navigational"
          controlIcon="chevronRight"
          onClick={goToSection('theme')}
        />
      </ListView>
    </Sheets>
  );
};
